import React, { useEffect, useState, useMemo} from "react";
import AuthCheck from "../components/AuthCheck";
import {Button, Card, Col, Container, Modal, Row, Stack, Form, InputGroup} from "react-bootstrap";
import {Link, Navigate} from "react-router-dom";
import tableDataModules from "../controller/tableDataModules";
import TableList, {TableDataTypes} from "../components/table/TableList";
import {faEdit, faTrash, faSearch, faCog, faUserShield} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {useForm} from "react-hook-form";

function Accounts() {
    // State for input fields (removes need for <Form>)
    const [studentNumber, setStudentNumber] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [year, setYear] = useState("");
    const [fulltime, setFulltime] = useState(false);
    const [cases, setCases] = useState({});

    const [tblModule,setTblModule] = useState([]);
    const [rawStudentData, setRawStudentData] = useState([]);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showRolesModal, setShowRolesModal] = useState(false);
    const [editingStudent, setEditingStudent] = useState(null);
    const [selectedRoles, setSelectedRoles] = useState([]);
    
    // Filter states
    const [searchName, setSearchName] = useState("");
    const [searchNumber, setSearchNumber] = useState("");
    const [roleFilter, setRoleFilter] = useState("");

    // Function to format student data
    const formatStudentData = (students) => {
        return students.map(student => [
            student.studentNumber,    // Student Number
            student.firstName + ' '+ student.lastName,
            student.year,             // Year
            student.fulltime ? "V" : "D",
            student.roles && Array.isArray(student.roles) ? student.roles.join(", ") : (student.role || "STUDENT"), // Role
            student.submissionCount,
            [
                {
                    icon: (
                        <button
                            onClick={() => handleRoleEdit(student)}
                            style={{ border: "none", background: "none", cursor: "pointer", marginRight: "10px" }}
                            title="Manage roles"
                        >
                            <FontAwesomeIcon icon={faUserShield} color="purple" />
                        </button>
                    )
                },
                {
                    icon: (
                        <button
                            onClick={() => handleEdit(student)}
                            style={{ border: "none", background: "none", cursor: "pointer", marginRight: "10px" }}
                            title="Edit basic info"
                        >
                            <FontAwesomeIcon icon={faEdit} color="blue" />
                        </button>
                    )
                },
                {
                    icon: (
                        <button
                            onClick={() => window.location.href = `/user-settings?userId=${student.id}`}
                            style={{ border: "none", background: "none", cursor: "pointer", marginRight: "10px" }}
                            title="User settings"
                        >
                            <FontAwesomeIcon icon={faCog} color="green" />
                        </button>
                    )
                },
                {
                    icon: (
                        <button
                            onClick={() => handleDelete(student.studentNumber)}
                            style={{ border: "none", background: "none", cursor: "pointer" }}
                            title="Delete user"
                        >
                            <FontAwesomeIcon icon={faTrash} color="red" />
                        </button>
                    )
                }
            ]
        ]);
    };

    useEffect(()=>{
        tableDataModules.getStudents().then(val => {
            if (val && val.data) {
                setRawStudentData(val.data);
                setTblModule(formatStudentData(val.data));
            }
        }).catch(err => console.error("Error fetching students:", err));
    }, []);
    
    // Filter data based on search criteria
    const filteredData = useMemo(() => {
        let filtered = rawStudentData;
        
        if (searchName) {
            filtered = filtered.filter(student => 
                (student.firstName + ' ' + student.lastName).toLowerCase().includes(searchName.toLowerCase())
            );
        }
        
        if (searchNumber) {
            filtered = filtered.filter(student => 
                student.studentNumber.toString().includes(searchNumber)
            );
        }
        
        if (roleFilter) {
            filtered = filtered.filter(student => {
                // Check if student has roles array, otherwise fallback to role string
                if (student.roles && Array.isArray(student.roles)) {
                    return student.roles.includes(roleFilter);
                }
                return (student.role || "STUDENT").includes(roleFilter);
            });
        }
        
        return formatStudentData(filtered);
    }, [rawStudentData, searchName, searchNumber, roleFilter]);
    
    // Check if user has admin role
    const userRole = localStorage.getItem("role");
    if (!userRole || !userRole.includes("ADMIN")) {
        return <Navigate to="/" />;
    }
    const handleEdit = (student) => {
        setEditingStudent(student);
        setShowEditModal(true);
        tableDataModules.getCasesFromStudent(student.studentNumber).then(val => {
            setCases(val)
        });
        // Manually set each field value
        setStudentNumber(student.studentNumber);
        setFirstName(student.firstName);
        setLastName(student.lastName);
        setYear(student.year);
        setFulltime(student.fulltime);
    };

    const handleRoleEdit = (student) => {
        setEditingStudent(student);
        setShowRolesModal(true);
        // Set current roles - handle both array format and single role string
        const currentRoles = student.roles && Array.isArray(student.roles) ?
            student.roles :
            (student.role ? [student.role] : ["STUDENT"]);
        setSelectedRoles(currentRoles);
    };

    const handleRoleToggle = (role) => {
        setSelectedRoles(prev => {
            if (prev.includes(role)) {
                return prev.filter(r => r !== role);
            } else {
                return [...prev, role];
            }
        });
    };

    const submitRolesForm = () => {
        if (!editingStudent) return;

        tableDataModules.updateUserRoles(editingStudent.studentNumber, selectedRoles)
            .then(() => {
                alert("User roles updated successfully!");
                setShowRolesModal(false);
                refreshTableData();
            })
            .catch(error => alert("Error updating roles: " + error.message));
    };
// Function to refresh table data
    const refreshTableData = () => {
        tableDataModules.getStudents().then(val => {
            if (val && val.data) {
                setRawStudentData(val.data);
                setTblModule(formatStudentData(val.data));
            }
        }).catch(err => console.error("Error fetching students:", err));
    };

    // Function to handle form submission (Save changes)
    const submitStudentForm = (data) => {
        console.log("Updated Student Data:", data);
        // Call API to update student
        tableDataModules.updateStudent(editingStudent.studentNumber, data)
            .then(() => {
                alert("Student details updated successfully!");
                setShowEditModal(false);
                refreshTableData();
            })
            .catch(error => alert("Error updating student: " + error.message));
    };

    // Function to handle Delete with confirmation prompt
    const handleDelete = (studentNumber) => {
        const confirmDelete = window.confirm(
            `Are you sure you want to delete Student ${studentNumber} and all related submissions?`
        );
        if (confirmDelete) {
            // Call API to delete student
            tableDataModules.deleteStudent(studentNumber)
                .then(() => {
                    alert(`Student ${studentNumber} has been deleted.`);
                    // Refresh the list
                    refreshTableData();
                    setShowEditModal(false);
                })
                .catch(error => console.log("Error deleting student: " + error.message));
        }
    };

    // Function to reset password
    const handleResetPassword = () => {
        if (editingStudent) {
            console.log(`Resetting password for ${editingStudent.studentNumber}`);
            // Call API to reset password (no password needed in request)
            tableDataModules.resetPassword(editingStudent.studentNumber)
                .then(() => alert(`Password has been reset for ${editingStudent.studentNumber}`))
                .catch(error => alert("Error resetting password: " + error.message));
        }
    };
    const getColumnData = () => {
        return [
            {name: "Student nr.", type: TableDataTypes.text, size: 2},
            {name: "Name", type: TableDataTypes.title, size: 2},
            {name: "Year", type: TableDataTypes.text, size: 1},
            {name: "VD", type: TableDataTypes.text, size: 1},
            {name: "Role", type: TableDataTypes.text, size: 2},
            {name: "Submissions", type: TableDataTypes.text, size: 2},
            {name: "", type: TableDataTypes.button, size: 1, sortable: false},
        ]
    };

    return(
        <>
            <AuthCheck/>
            <Container>
                <Row style={{  marginTop: "20px"}}>
                    <Col xs={{span: 12}}>
                        <Row className="mb-3">
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Search by Name</Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter student name..."
                                            value={searchName}
                                            onChange={(e) => setSearchName(e.target.value)}
                                        />
                                        <InputGroup.Text>
                                            <FontAwesomeIcon icon={faSearch} />
                                        </InputGroup.Text>
                                    </InputGroup>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Search by Student Number</Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter student number..."
                                            value={searchNumber}
                                            onChange={(e) => setSearchNumber(e.target.value)}
                                        />
                                        <InputGroup.Text>
                                            <FontAwesomeIcon icon={faSearch} />
                                        </InputGroup.Text>
                                    </InputGroup>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Filter by Role</Form.Label>
                                    <Form.Select
                                        value={roleFilter}
                                        onChange={(e) => setRoleFilter(e.target.value)}
                                    >
                                        <option value="">All Roles</option>
                                        <option value="STUDENT">Student</option>
                                        <option value="TEACHER">Teacher</option>
                                        <option value="ADMIN">Admin</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <TableList columnData={getColumnData()} rowData={filteredData} selected={null} />
                        </Row>
                    </Col>

                </Row>
            </Container>
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Student</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {editingStudent ? (
                        <>
                            <label>Student Number</label>
                            <input
                                type="text"
                                value={studentNumber}
                                disabled
                                className="form-control"
                            />

                            <label>First Name</label>
                            <input
                                type="text"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                className="form-control"
                            />

                            <label>Last Name</label>
                            <input
                                type="text"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                className="form-control"
                            />

                            <label>Year</label>
                            <input
                                type="text"
                                value={year}
                                onChange={(e) => setYear(e.target.value)}
                                className="form-control"
                            />

                            <div className="form-check form-switch mt-2">
                                <label className="form-check-label" htmlFor="fulltimeSwitch">
                                    Full-Time
                                </label>
                                <input
                                    type="checkbox"
                                    checked={fulltime}
                                    onChange={(e) => setFulltime(e.target.checked)}
                                    className="form-check-input"
                                    id="fulltimeSwitch"
                                />

                            </div>
                            <hr/>

                            {/* Display cases */}
                            <label>Cases:</label>
                            {cases && Object.keys(cases).length > 0 ? (
                                <ul className="list-group">
                                    {Object.entries(cases).map(([key, value]) => (
                                        <li key={key} className="list-group-item">
                                            <strong>{key}</strong>: {value[0].name}
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-muted">No cases available</p>
                            )}
                            <hr/>
                            <Stack gap={1}>
                                <Button variant="warning" onClick={handleResetPassword} style={{ marginTop: "10px" }}>
                                    Reset Password to 'alpine'
                                </Button>
                                <Button variant="danger" onClick={()=>{handleDelete(studentNumber)}} style={{ marginTop: "10px" }}>
                                    Delete
                                </Button>
                            </Stack>

                        </>
                    ) : (
                        <p>Loading student data...</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditModal(false)}>Close</Button>
                    <Button variant="primary" onClick={() => {
                        // Prepare updated student data
                        const updatedData = {
                            firstName: firstName,
                            lastName: lastName,
                            year: year,
                            fulltime: fulltime
                        };
                        submitStudentForm(updatedData);
                    }}>Save Changes</Button>
                </Modal.Footer>
            </Modal>

            {/* Roles Management Modal */}
            <Modal show={showRolesModal} onHide={() => setShowRolesModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Manage User Roles</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {editingStudent ? (
                        <>
                            <p><strong>User:</strong> {editingStudent.firstName} {editingStudent.lastName} ({editingStudent.studentNumber})</p>
                            <p><strong>Current roles:</strong></p>
                            <div className="mb-3">
                                {["STUDENT", "TEACHER", "ADMIN"].map(role => (
                                    <div key={role} className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id={`role-${role}`}
                                            checked={selectedRoles.includes(role)}
                                            onChange={() => handleRoleToggle(role)}
                                        />
                                        <label className="form-check-label" htmlFor={`role-${role}`}>
                                            {role}
                                        </label>
                                    </div>
                                ))}
                            </div>
                            <small className="text-muted">
                                Select one or more roles for this user. At least one role should be selected.
                            </small>
                        </>
                    ) : (
                        <p>Loading user data...</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRolesModal(false)}>Cancel</Button>
                    <Button
                        variant="primary"
                        onClick={submitRolesForm}
                        disabled={selectedRoles.length === 0}
                    >
                        Update Roles
                    </Button>
                </Modal.Footer>
            </Modal>

        </>
    )
}

export default Accounts;